import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import FeedbackModal from '../components/FeedbackModal';
import useFigureMatchGameLogic from '../hooks/useFigureMatchGameLogic';
import styles from './FigureMatchGame.module.css';
import { usePreloadedData } from "../context/PreloadContext";

// --- Feedback Modal Constant ---
const FEEDBACK_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfCYQQ3NJS3nRLoVB9Q1LtELQxQizAD37t1LMwJ_5-ei2dryA/viewform?usp=dialog";
// --- End Feedback Modal Constant ---

const FigureMatchGame = () => {
    const navigate = useNavigate();
    const [showInstructions, setShowInstructions] = useState(true);
    const { preloadedData } = usePreloadedData();

    // Call the new custom hook to get all the game state and functions
    const {
        loading,
        error,
        cards,
        matchedPairs,
        lives,
        moves,
        gameOver,
        shakingCards,
        currentFigureBiography,
        figuresInCurrentGame,
        showBiographyModal,
        showReviewModal,
        showStreakPopup,
        streakMessage,
        showGameOverPopup,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal,
        startTimeRef,
        handleCardClick,
        resetGame,
        closeBiographyModal,
        closeReviewModal,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
        calculateScore,
    } = useFigureMatchGameLogic({ navigate, preloadedData });

    // Handle initial game start after instructions are dismissed
    const handleProceedToGame = useCallback(() => {
        setShowInstructions(false);
    }, []);

    const backgroundStyle = {
        backgroundImage: `url("/images/figure.jpg")`,
    };

    if (loading) {
        return (
            <div className={styles.gameContainer} style={backgroundStyle}>
                <LoadingSpinner message="Loading Islamic Figures Match Game..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.gameContainer} style={backgroundStyle}>
                <p className={styles.errorMessage}>Error: {error}</p>
                <button onClick={resetGame} className={styles.button}>Retry Game</button>
            </div>
        );
    }

    return (
        <div className={styles.gameContainer} style={backgroundStyle}>
            {/* Main title */}
            <h2 className={styles.title}>Islamic Figures Match</h2>

            {/* Game Info - Visible when game is active */}
            {!showInstructions && !gameOver && (
                <>
                    <div className={styles.gameInfo}>
                        <p>Moves: {moves} | Pairs Matched: {matchedPairs.length} / {new Set(cards.map(c => c.pairId)).size}</p>
                        <div className={styles.livesContainer}>
                            {[...Array(5)].map((_, i) => (
                                <span
                                    key={i}
                                    className={`${styles.heartIcon} ${i < lives ? styles.filledHeart : styles.emptyHeart}`}
                                >
                                    ❤️
                                </span>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* --- Conditional Rendering for Popups and Game Board --- */}
            {showInstructions ? (
                <div className={styles.instructionsPopup}>
                    <h3>How to Play:</h3>
                    <ul>
                        <li>Click any two cards (one Name and one Attribute) to flip the Attribute card and try to find a match.</li>
                        <li>If the Name and Attribute match the same figure, both cards stay open and become darker. If not, the Attribute card flips back.</li>
                        <li>Match all pairs to win!</li>
                    </ul>
                    <button className={styles.button} onClick={handleProceedToGame}>
                        Start Game
                    </button>
                </div>
            ) : gameOver ? (
                <div className={styles.gameOverSection}>
                    {showGameOverPopup ? (
                        <>
                            <h3 className={styles.gameOverTitle}>Game Over! 💔</h3>
                            <p className={styles.gameOverMessage}>Aim for better next time Insha'Allah.</p>
                            <p className={styles.finalScore}>Final Score: {calculateScore(matchedPairs.length, Math.floor((Date.now() - startTimeRef.current) / 1000), new Set(cards.map(c => c.pairId)).size)}</p>
                        </>
                    ) : (
                        <>
                            <p className={styles.finalScoreMessage}>Congratulations! All pairs matched!</p>
                            <p className={styles.finalScore}>Final Score: {calculateScore(matchedPairs.length, Math.floor((Date.now() - startTimeRef.current) / 1000), new Set(cards.map(c => c.pairId)).size)}</p>
                        </>
                    )}
                    <button onClick={() => closeReviewModal(true)} className={styles.button} style={{ marginRight: '10px' }}>
                        Review Figures
                    </button>
                    <button onClick={resetGame} className={styles.button}>Play Again</button>
                </div>
            ) : (
                <>
                    <div className={styles.cardsGrid}>
                        {cards.map((card) => (
                            <div
                                key={card.instanceId}
                                className={`${styles.card} ${card.isFlipped ? styles.flipped : ''} ${card.type === 'name' ? styles.nameCard : styles.attributeCard} ${card.isMatched ? styles.matched : ''} ${shakingCards.includes(card.instanceId) ? styles.shaking : ''}`}
                                onClick={() => handleCardClick(card)}
                            >
                                <div className={styles.cardInner}>
                                    <div className={styles.cardFront}>
                                        {card.type === 'name' ? (
                                            <p className={styles.cardText}>{card.content}</p>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" className={styles.cardIcon}>
                                                <path fill="currentColor" d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32h82.7L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32H320zM80 64C35.8 64 0 99.8 0 144V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V352H288c-17.7 0-32-14.3-32-32s14.3-32 32-32h96V144c0-35.3-28.7-64-64-64H80z"/>
                                            </svg>
                                        )}
                                    </div>
                                    <div className={styles.cardBack}>
                                        <p className={styles.cardText}>{card.content}</p>
                                        {card.reference && <p className={styles.cardReference}>{card.reference}</p>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Biography Modal */}
            {showBiographyModal && currentFigureBiography && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <h3>{currentFigureBiography.content}</h3>
                        <p className={styles.modalBiography}>{currentFigureBiography.biography}</p>
                        <button onClick={closeBiographyModal} className={styles.button}>Close</button>
                    </div>
                </div>
            )}

            {/* Post-Game Review Modal */}
            {showReviewModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.reviewModalContent}>
                        <h3>Review Figures</h3>
                        <div className={styles.reviewFiguresList}>
                            {figuresInCurrentGame.map(figure => (
                                <div key={figure.pairId} className={styles.reviewFigureItem}>
                                    <h4>{figure.content}</h4>
                                    <p>{figure.biography}</p>
                                    <p className={styles.reviewFigureReference}>{figure.reference}</p>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => closeReviewModal()} className={styles.button}>Close Review</button>
                        <button onClick={resetGame} className={styles.button} style={{ marginLeft: '10px' }}>Play Again</button>
                    </div>
                </div>
            )}

            {/* STREAK POPUP */}
            {showStreakPopup && (
                <div className={styles.streakPopup}>
                    <p>{streakMessage}</p>
                </div>
            )}

            {/* ENCOURAGEMENT POPUP */}
            {showEncouragementPopup && (
                <div className={styles.encouragementPopup}>
                    <p>{encouragementMessage}</p>
                </div>
            )}

            {/* FEEDBACK MODAL */}
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => handleCloseFeedbackModal()}
                onDismissPermanently={() => handleDismissFeedbackPermanently()}
                formUrl={FEEDBACK_FORM_URL}
                gameName="Islamic Figures Match"
            />
        </div>
    );
};

export default FigureMatchGame;
